import React, { useState, useCallback, useEffect } from "react";
import styled, { css } from "styled-components";

const DropZone = styled.div<{ $hasImage: boolean }>`
  width: 100%;
  height: 100%;
  border-radius: 5px;
  text-align: center;
  color: #666;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  &:hover {
    border-color: #999;
    color: #333;
  }

  ${(props) =>
    props.$hasImage &&
    css`
      border: none;
      padding: 0;
    `}
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  overflow: hidden;
`;

const PreviewWrapper = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const DropNode = styled(PreviewWrapper)`
  background-color: rgba(0, 0, 0, 0.6);
`;

const ErrorMessage = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: #ff4444;
  color: white;
  padding: 8px;
  text-align: center;
  animation: fadeOut 3s forwards;
  z-index: 10;

  @keyframes fadeOut {
    0% {
      opacity: 1;
    }
    70% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  }
`;

const DefaultGuideMessage = styled.div`
  text-align: center;
  color: #666;

  > ul {
    list-style: none;
    padding: 0;
    margin: 8px 0 0;
    font-size: 0.9em;

    > li {
      margin: 4px 0;
    }
  }
`;

const ZoneBorder = styled.div`
  width: 100%;
  height: 100%;
  border: 2px dashed #cccccc;
  border-radius: 5px;
`;

interface ChildrenObject {
  empty: React.ReactNode;
  preview?: React.ReactNode;
}

interface ImageDropzoneProps {
  onImageUpload: (file: File) => void;
  children?: React.ReactNode | ChildrenObject;
  maxSizeMB?: number;
  acceptedTypes?: string[];
  minWidth?: number;
  maxWidth?: number;
  minHeight?: number;
  maxHeight?: number;
  defaultUrl?: string;
}

const DEFAULT_MIN_SIZE = 400;
const DEFAULT_MAX_SIZE = 1600;

const ImageDropzone: React.FC<ImageDropzoneProps> = ({
  onImageUpload,
  children,
  maxSizeMB = 5,
  acceptedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"],
  minWidth = DEFAULT_MIN_SIZE,
  maxWidth = DEFAULT_MAX_SIZE,
  minHeight = DEFAULT_MIN_SIZE,
  maxHeight = DEFAULT_MAX_SIZE,
  defaultUrl,
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const showError = (message: string) => {
    setError(message);
    setTimeout(() => setError(null), 3000);
  };

  const validateFileSize = (file: File): string | null => {
    if (file.size > maxSizeMB * 1024 * 1024) {
      return `파일 크기는 ${maxSizeMB}MB 이하여야 합니다.`;
    }
    return null;
  };

  const validateFileType = (file: File): string | null => {
    if (!acceptedTypes.includes(file.type)) {
      return `이미지 파일만 업로드 가능합니다. (${acceptedTypes
        .map((type) => type.replace("image/", "").toUpperCase())
        .join(", ")})`;
    }
    return null;
  };

  const validateImageDimensions = (
    width: number,
    height: number
  ): string | null => {
    if (width < minWidth || height < minHeight) {
      return `이미지 크기가 너무 작습니다. (최소 ${minWidth}x${minHeight}px)`;
    }
    if (width > maxWidth || height > maxHeight) {
      return `이미지 크기가 너무 큽니다. (최대 ${maxWidth}x${maxHeight}px)`;
    }
    return null;
  };

  const validateFile = async (file: File): Promise<string | null> => {
    const typeError = validateFileType(file);
    if (typeError) return typeError;

    const sizeError = validateFileSize(file);
    if (sizeError) return sizeError;

    return new Promise((resolve) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(img.src);
        resolve(validateImageDimensions(img.width, img.height));
      };
      img.onerror = () => {
        URL.revokeObjectURL(img.src);
        resolve("이미지 크기를 확인할 수 없습니다.");
      };
    });
  };

  const handleFile = useCallback(
    async (file: File) => {
      const errorMessage = await validateFile(file);
      if (errorMessage) {
        showError(errorMessage);
        return;
      }

      onImageUpload(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    },
    [
      onImageUpload,
      maxSizeMB,
      acceptedTypes,
      minWidth,
      maxWidth,
      minHeight,
      maxHeight,
    ]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const file = e.dataTransfer.files[0];
      if (file) {
        handleFile(file);
      }
    },
    [handleFile]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  }, []);

  const handleClick = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = acceptedTypes.join(",");
    input.onchange = (e: Event) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        handleFile(file);
      }
    };
    input.click();
  }, [handleFile, acceptedTypes]);

  const isChildrenObject = (
    children: React.ReactNode | ChildrenObject
  ): children is ChildrenObject => {
    return (
      typeof children === "object" && children !== null && "empty" in children
    );
  };

  const renderChildren = () => {
    if (!children) {
      return (
        <DefaultGuideMessage>
          <div>이미지를 업로드하세요</div>
          <ul>
            <li>
              ✓{" "}
              {acceptedTypes
                .map((type) => type.replace("image/", "").toUpperCase())
                .join(", ")}
            </li>
            <li>
              ✓ {minWidth}x{minHeight}px ~ {maxWidth}x{maxHeight}px
            </li>
            <li>✓ 최대 {maxSizeMB}MB</li>
          </ul>
        </DefaultGuideMessage>
      );
    }

    return isChildrenObject(children) ? children.empty : children;
  };

  useEffect(() => {
    if (defaultUrl) {
      setPreviewUrl(defaultUrl);
    } else if (defaultUrl === "" || !defaultUrl) {
      setPreviewUrl(null);
    }
  }, [defaultUrl]);
  return (
    <DropZone
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onClick={handleClick}
      $hasImage={!!previewUrl}
    >
      {previewUrl ? (
        <PreviewWrapper className="preview-wrapper">
          <PreviewImage src={previewUrl} alt="Uploaded preview" />

          {children && isChildrenObject(children) && (
            <DropNode>{children.preview}</DropNode>
          )}
        </PreviewWrapper>
      ) : (
        renderChildren()
      )}
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </DropZone>
  );
};

export default ImageDropzone;
